import { useEffect, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { BarChart3, MousePointerClick, Percent, Users } from "lucide-react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import api from "@/lib/api";
import StatCard from "@/components/admin/StatCard";

function BreakdownRow({ name, count, total }) {
  const pct = total ? Math.round((count / total) * 100) : 0;
  return (
    <div data-testid={`breakdown-${name}`}>
      <div className="flex items-baseline justify-between text-sm">
        <span className="capitalize">{name}</span>
        <span className="tabular-nums text-zinc-500">
          {count} &middot; {pct}%
        </span>
      </div>
      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-zinc-100">
        <div className="h-full rounded-full bg-black transition-all duration-700" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function Analytics() {
  const [overview, setOverview] = useState(null);
  const [series, setSeries] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/analytics/overview"),
      api.get("/analytics/timeseries?days=30"),
      api.get("/analytics/recent-clicks"),
    ])
      .then(([o, s, r]) => {
        setOverview(o.data);
        setSeries(s.data);
        setRecent(r.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center" data-testid="analytics-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-black/10 border-t-black" />
      </div>
    );
  }

  const totalDevices = (overview?.devices || []).reduce((a, d) => a + d.count, 0);
  const totalStores = (overview?.stores || []).reduce((a, s) => a + s.count, 0);

  return (
    <div className="space-y-8" data-testid="analytics-page">
      <div>
        <h1 className="font-heading text-3xl font-semibold tracking-tight">Analytics</h1>
        <p className="mt-1 text-sm text-zinc-500">Privacy-conscious stats: no personal data is collected.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={Users} label="Total visits" value={overview?.total_visits ?? 0} sub={`${overview?.visits_7d ?? 0} last 7 days`} testid="stat-total-visits" />
        <StatCard icon={MousePointerClick} label="Total clicks" value={overview?.total_clicks ?? 0} sub={`${overview?.clicks_7d ?? 0} last 7 days`} testid="stat-total-clicks" />
        <StatCard icon={Percent} label="Overall CTR" value={`${overview?.ctr ?? 0}%`} sub="all time" testid="stat-overall-ctr" />
        <StatCard icon={BarChart3} label="Products live" value={`${overview?.published_products ?? 0}/${overview?.total_products ?? 0}`} sub="published / total" testid="stat-products-live" />
      </div>

      <div className="rounded-3xl border border-black/5 bg-white p-6" data-testid="analytics-chart-card">
        <h2 className="font-heading text-lg font-medium tracking-tight">Clicks per day</h2>
        <p className="text-xs text-zinc-400">Last 30 days</p>
        <div className="mt-6 h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="analyticsFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#111111" stopOpacity={0.15} />
                  <stop offset="100%" stopColor="#111111" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#a1a1aa" }} tickLine={false} axisLine={false} interval={4} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#a1a1aa" }} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ borderRadius: 16, border: "1px solid rgba(0,0,0,0.08)", fontSize: 12 }} cursor={{ stroke: "rgba(0,0,0,0.15)" }} />
              <Area type="monotone" dataKey="clicks" stroke="#111111" strokeWidth={2} fill="url(#analyticsFill)" name="Clicks" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-3xl border border-black/5 bg-white p-6" data-testid="device-breakdown-card">
          <h2 className="font-heading text-lg font-medium tracking-tight">Devices</h2>
          <p className="text-xs text-zinc-400">Where clicks come from</p>
          {totalDevices === 0 ? (
            <p className="mt-6 rounded-2xl bg-zinc-50 py-8 text-center text-sm text-zinc-500" data-testid="devices-empty-state">No click data yet.</p>
          ) : (
            <div className="mt-6 space-y-4">
              {overview.devices.map((d) => (
                <BreakdownRow key={d.name} name={d.name} count={d.count} total={totalDevices} />
              ))}
            </div>
          )}
        </div>

        <div className="rounded-3xl border border-black/5 bg-white p-6" data-testid="store-breakdown-card">
          <h2 className="font-heading text-lg font-medium tracking-tight">Stores</h2>
          <p className="text-xs text-zinc-400">Clicks per platform</p>
          {totalStores === 0 ? (
            <p className="mt-6 rounded-2xl bg-zinc-50 py-8 text-center text-sm text-zinc-500" data-testid="stores-empty-state">No click data yet.</p>
          ) : (
            <div className="mt-6 space-y-4">
              {overview.stores.map((s) => (
                <BreakdownRow key={s.name} name={s.name} count={s.count} total={totalStores} />
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="overflow-hidden rounded-3xl border border-black/5 bg-white" data-testid="recent-clicks-card">
        <div className="p-6 pb-0">
          <h2 className="font-heading text-lg font-medium tracking-tight">Recent clicks</h2>
          <p className="text-xs text-zinc-400">Latest 20 Buy Now taps</p>
        </div>
        {recent.length === 0 ? (
          <p className="m-6 rounded-2xl bg-zinc-50 py-8 text-center text-sm text-zinc-500" data-testid="recent-clicks-empty-state">
            No clicks yet. They will appear here the moment someone taps Buy Now.
          </p>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm" data-testid="recent-clicks-table">
              <thead>
                <tr className="border-b border-black/5 text-left text-xs uppercase tracking-wider text-zinc-400">
                  <th className="px-6 py-3 font-medium">Product</th>
                  <th className="px-4 py-3 font-medium">Store</th>
                  <th className="px-4 py-3 font-medium">Device</th>
                  <th className="px-4 py-3 font-medium">Source</th>
                  <th className="px-6 py-3 font-medium">When</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((c) => (
                  <tr key={c.id} className="border-b border-black/5 last:border-0" data-testid={`recent-click-${c.id}`}>
                    <td className="max-w-[220px] truncate px-6 py-4 font-medium">{c.product_name}</td>
                    <td className="px-4 py-4 text-zinc-500">{c.store}</td>
                    <td className="px-4 py-4 capitalize text-zinc-500">{c.device_type}</td>
                    <td className="max-w-[160px] truncate px-4 py-4 text-zinc-500">{c.referrer || "direct"}</td>
                    <td className="whitespace-nowrap px-6 py-4 tabular-nums text-zinc-500">
                      {formatDistanceToNow(new Date(c.timestamp), { addSuffix: true })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
